import { cn, Icon } from "@nexus/ui"
import type { CardStat, DrilldownDotColor } from "../lib/drilldown-card.types"
import { unsignedTrendLabel } from "../lib/drilldown-card-trend"

export interface DrilldownCardStatProps {
  dotColor: DrilldownDotColor
  label: string
  value: string
  trendLabel?: CardStat["trendLabel"]
  trendTone?: CardStat["trendTone"]
  trendDirection?: CardStat["trendDirection"]
  trendAriaLabel?: string
}

const PILL_TONE_CLASS: Record<NonNullable<CardStat["trendTone"]>, string> = {
  green: "bg-success-subtle text-success-subtle-foreground",
  red: "bg-error-subtle text-error-subtle-foreground",
  neutral: "bg-muted text-muted-foreground",
}

const COMPACT_TONE_CLASS: Record<NonNullable<CardStat["trendTone"]>, string> = {
  green: "text-success",
  red: "text-error",
  neutral: "text-muted-foreground",
}

function hasTrend(trendLabel: string | null | undefined): trendLabel is string {
  return trendLabel != null && trendLabel.length > 0
}

function TrendPill({
  trendLabel,
  trendTone,
  trendDirection,
  trendAriaLabel,
}: {
  trendLabel: string
  trendTone: NonNullable<CardStat["trendTone"]>
  trendDirection: CardStat["trendDirection"]
  trendAriaLabel?: string
}) {
  return (
    <span
      data-testid="drilldown-card-trend-pill"
      aria-label={trendAriaLabel}
      className={cn(
        "inline-flex h-4 shrink-0 items-center gap-0.5 rounded-[6px] px-1.5 text-[10px] font-medium",
        PILL_TONE_CLASS[trendTone],
      )}
    >
      {trendDirection === "up" ? <Icon name="arrow-up-right" size="xs" color="inherit" /> : null}
      {trendDirection === "down" ? <Icon name="arrow-down-right" size="xs" color="inherit" /> : null}
      {unsignedTrendLabel(trendLabel)}
    </span>
  )
}

/**
 * Primary ("big") stat row. Without a trend this is a single line (dot + label
 * left, value right) — competitor cards stay on this layout. With a trend,
 * Figma AssetCard stacks the label + polarity pill above the large value.
 */
export function DrilldownCardStatBig({
  dotColor,
  label,
  value,
  trendLabel,
  trendTone = "neutral",
  trendDirection = null,
  trendAriaLabel,
}: DrilldownCardStatProps) {
  if (!hasTrend(trendLabel)) {
    return (
      <div className="flex items-center justify-between gap-1 px-2 py-1.5">
        <span className="flex items-center gap-1">
          <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        </span>
        <span className="text-base font-semibold text-foreground">{value}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-0.5 px-2 py-1.5">
      <div className="flex items-center justify-between gap-1">
        <span className="flex items-center gap-1">
          <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        </span>
        <TrendPill
          trendLabel={trendLabel}
          trendTone={trendTone}
          trendDirection={trendDirection}
          trendAriaLabel={trendAriaLabel}
        />
      </div>
      <span className="text-base font-semibold text-foreground">{value}</span>
    </div>
  )
}

/**
 * Secondary ("compact") stat row: one line. With a trend, a signed percent sits
 * beside the value in the polarity colour (no arrow).
 */
export function DrilldownCardStatCompact({
  dotColor,
  label,
  value,
  trendLabel,
  trendTone = "neutral",
  trendAriaLabel,
}: DrilldownCardStatProps) {
  return (
    <div className="flex items-center justify-between gap-1 px-2">
      <span className="flex items-center gap-1">
        <span className="size-1 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
        <span className="text-[11px] text-muted-foreground">{label}</span>
      </span>
      <span className="flex items-center gap-1">
        <span className="text-[11px] font-medium text-foreground">{value}</span>
        {hasTrend(trendLabel) ? (
          <span
            data-testid="drilldown-card-trend-pct"
            aria-label={trendAriaLabel}
            className={cn("text-[11px] font-medium", COMPACT_TONE_CLASS[trendTone])}
          >
            {trendLabel}
          </span>
        ) : null}
      </span>
    </div>
  )
}
